import { createSlice } from "@reduxjs/toolkit";

import { SITE_PROVENANCE } from "../../constants/sharepointAccess";

export const EMPTY_POLICY = {
  revision: 0,
  restricted: false,
  requiresReselection: false,
  maxSites: null,
  sites: [],
  updatedAt: null,
  updatedBy: null,
};

export const SITE_VIEWS = { all: "all", allowed: "allowed" };

export const INITIAL_DISCOVERY = {
  sites: [],
  nextSkipToken: null,
  search: "",
  appliedSearch: "",
  isSearchActive: false,
  searchPagination: { hasMore: false, nextStartRow: null },
  isLoading: false,
  error: null,
  loadMoreError: false,
};

export const INITIAL_STATE = {
  policy: null,
  isLoadingPolicy: false,
  draft: { restricted: false, sites: [] },
  isSaving: false,
  saveError: null,
  hasConflict: false,
  isAuthenticated: false,
  reauthenticationRequired: false,
  isConnecting: false,
  connectNotice: null,
  view: SITE_VIEWS.all,
  discovery: INITIAL_DISCOVERY,
};

const toDraftSite = (
  { id, name, host, sitePath, lastModified, provenance },
  forcedProvenance
) => ({
  id,
  name,
  host,
  sitePath,
  lastModified: lastModified ?? null,
  provenance: forcedProvenance || provenance || null,
});

const draftFromPolicy = (policy) => ({
  restricted: policy.restricted,
  sites: policy.sites.map((site) => toDraftSite(site, SITE_PROVENANCE.policy)),
});

export const allowedSitesSlice = createSlice({
  name: "sharepointAllowedSites",
  initialState: INITIAL_STATE,
  reducers: {
    setAllowedSitesPolicy: (state, { payload }) => {
      state.policy = { ...EMPTY_POLICY, ...payload };
      state.draft = draftFromPolicy(state.policy);
      state.saveError = null;
      state.hasConflict = false;
    },
    setAllowedSitesPolicyLoading: (state, { payload }) => {
      state.isLoadingPolicy = Boolean(payload);
    },
    setDraftRestricted: (state, { payload }) => {
      state.draft.restricted = Boolean(payload);
    },
    addDraftSites: (state, { payload = [] }) => {
      const known = new Set(state.draft.sites.map((site) => site.id));
      payload
        .filter((site) => !known.has(site.id))
        .forEach((site) => state.draft.sites.push(toDraftSite(site)));
    },
    removeDraftSites: (state, { payload = [] }) => {
      const removed = new Set(payload);
      state.draft.sites = state.draft.sites.filter(
        (site) => !removed.has(site.id)
      );
    },
    setAllowedSitesSaving: (state, { payload }) => {
      state.isSaving = Boolean(payload);
    },
    setAllowedSitesSaveError: (state, { payload }) => {
      state.saveError = payload || null;
    },
    setAllowedSitesConflict: (state, { payload }) => {
      state.hasConflict = Boolean(payload);
    },
    setAllowedSitesAuthenticated: (state, { payload }) => {
      state.isAuthenticated = Boolean(payload);
      if (state.isAuthenticated) {
        state.connectNotice = null;
        state.reauthenticationRequired = false;
      }
    },
    setAllowedSitesReauthenticationRequired: (state) => {
      state.reauthenticationRequired =
        state.reauthenticationRequired || state.isAuthenticated;
      state.isAuthenticated = false;
    },
    setSiteView: (state, { payload }) => {
      state.view =
        payload === SITE_VIEWS.allowed ? SITE_VIEWS.allowed : SITE_VIEWS.all;
    },
    setAllowedSitesConnecting: (state, { payload }) => {
      state.isConnecting = Boolean(payload);
    },
    setConnectNotice: (state, { payload }) => {
      state.connectNotice = payload || null;
    },
    setDiscoveryLoading: (state, { payload }) => {
      state.discovery.isLoading = Boolean(payload);
    },
    setDiscoveryError: (state, { payload }) => {
      state.discovery.error = payload || null;
    },
    setDiscoverySearch: (state, { payload }) => {
      state.discovery.search = payload || "";
    },
    setDiscoverySites: (state, { payload }) => {
      const { sites = [], nextSkipToken = null, append = false } = payload;
      state.discovery.sites = append
        ? [...state.discovery.sites, ...sites]
        : sites;
      state.discovery.nextSkipToken = nextSkipToken;
      state.discovery.isSearchActive = false;
      state.discovery.appliedSearch = "";
      state.discovery.searchPagination = INITIAL_DISCOVERY.searchPagination;
      state.discovery.loadMoreError = false;
    },
    setDiscoverySearchResults: (state, { payload }) => {
      const {
        sites = [],
        pagination = {},
        append = false,
        search = "",
      } = payload;
      state.discovery.sites = append
        ? [...state.discovery.sites, ...sites]
        : sites;
      state.discovery.isSearchActive = true;
      state.discovery.appliedSearch = search;
      state.discovery.nextSkipToken = null;
      state.discovery.searchPagination = {
        hasMore: Boolean(pagination.hasMore),
        nextStartRow: pagination.nextStartRow ?? null,
      };
      state.discovery.loadMoreError = false;
    },
    setDiscoveryLoadMoreError: (state, { payload }) => {
      state.discovery.loadMoreError = Boolean(payload);
    },
    resetDiscovery: (state) => {
      state.discovery = INITIAL_DISCOVERY;
    },
    resetAllowedSites: () => INITIAL_STATE,
  },
});

export const {
  setAllowedSitesPolicy,
  setAllowedSitesPolicyLoading,
  setDraftRestricted,
  addDraftSites,
  removeDraftSites,
  setAllowedSitesSaving,
  setAllowedSitesSaveError,
  setAllowedSitesConflict,
  setAllowedSitesAuthenticated,
  setAllowedSitesReauthenticationRequired,
  setSiteView,
  setAllowedSitesConnecting,
  setConnectNotice,
  setDiscoveryLoading,
  setDiscoveryError,
  setDiscoverySearch,
  setDiscoverySites,
  setDiscoverySearchResults,
  setDiscoveryLoadMoreError,
  resetDiscovery,
  resetAllowedSites,
} = allowedSitesSlice.actions;

export default allowedSitesSlice.reducer;
